import React from 'react';
import { useViewport } from '../lib/useViewport';

// MarketFeedRow — v2.2 "Dual-State Terminal" dense feed row.
//
// One row per active market. Macro only: title · lens · total OC
// pool · favourite (label @ price) · deepest outsider (label @ price)
// · momentum glow. No inline pledging, no vibe picker — those move
// into the MarketFocusModal that opens when the row is clicked.
//
// Momentum glow fires when the parent marks `momentumActive` (a recent
// pledge or vibe in the last few seconds). Pure CSS box-shadow pulse —
// no layout reflow.

export interface FeedOutcomeSummary {
  id:    string;
  label: string;
  price: number;
  color?: string;
}

interface Props {
  marketId:        string;
  title:           string;
  lens?:           string;
  ocVolume:        number;
  favorite?:       FeedOutcomeSummary;
  outsider?:       FeedOutcomeSummary;
  momentumActive?: boolean;
  active?:         boolean;
  // v2.3.1 Minting Cockpit Activation — PENDING_VOUCH rail. A freshly
  // minted market that hasn't cleared its vouch threshold appears in
  // the Global Feed as a greyed "STABILIZING…" row with vouch count.
  // Pledging is suppressed (row is display-only, no click-through) so
  // the citizen can see the pipeline without trading against a market
  // that isn't yet promoted to ACTIVE.
  pending?:        boolean;
  vouchCount?:     number;
  vouchTarget?:    number;
  // I-065 settled rail (DECISIONS.md 2026-05-17). When `settled` is
  // true the row replaces POOL/FAVOURITE/OUTSIDER with a single
  // SETTLED badge spanning the right side; opacity drops, border
  // shifts to neutral grey. Row stays clickable (opens the focus
  // modal with the frozen doughnut). Visibility window (48h from
  // settled_at) is enforced upstream in MultiPage.tsx fetchMarkets.
  settled?:        boolean;
  winningOutcome?: string | null;
  settledAt?:      string | null;  // ISO UTC timestamp
  // I-068 Brief 2 — oracle disclosure line. All three optional; when
  // oracleDisplayText is absent (or empty) the row renders without
  // the oracle line entirely. Decision A (Architect 2026-05-19):
  // first-class metadata below the title, above outcomes — not
  // fine-print. Decision B: holdbackHoursOverride is only set when
  // markets.holdback_hours overrides the registry default; default
  // 24h windows stay implicit.
  oracleDisplayText?:      string;
  oracleClassLabel?:       string;
  holdbackHoursOverride?:  number;
  onClick:         (marketId: string) => void;
}

function formatOC(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  if (n >= 100)       return n.toFixed(0);
  return n.toFixed(1);
}

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatSettledAtUTC(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day  = d.getUTCDate();
  const mon  = MONTHS_SHORT[d.getUTCMonth()];
  const hh   = String(d.getUTCHours()).padStart(2, '0');
  const mm   = String(d.getUTCMinutes()).padStart(2, '0');
  return `${day} ${mon} ${hh}:${mm} UTC`;
}

export default function MarketFeedRow({
  marketId, title, lens, ocVolume, favorite, outsider,
  momentumActive = false, active = false,
  pending = false, vouchCount, vouchTarget,
  settled = false, winningOutcome, settledAt,
  oracleDisplayText, oracleClassLabel, holdbackHoursOverride,
  onClick,
}: Props): React.JSX.Element {
  // 2026-05-21 narrow-viewport fix: below 768 px (single source of
  // truth, lib/useViewport) the row drops the four-column grid and
  // re-flows as a left-aligned wrapping stack. The minWidth:110 +
  // nowrap right-side cells overflow the viewport on iPad-width and
  // narrower because the grid floors can't shrink and labels can't
  // wrap. On mobile the cells go textAlign:left, lose their minWidth
  // floors, and their labels wrap. Desktop layout (>=768 px) is
  // unchanged.
  const { isMobile } = useViewport();
  // Pending markets suppress the oracle line entirely per brief §3.5 —
  // surfacing the binding before the market clears vouch is operational
  // noise. Settled rows still render the line but inherit the row's
  // muted opacity (brief §3.4); the SETTLED block in the middle column
  // takes visual priority.
  const showOracleLine = !pending && !!oracleDisplayText;
  const borderColor = pending
    ? '#334155'
    : settled
      ? '#475569'
      : active
        ? '#93c5fd'
        : momentumActive
          ? '#a78bfa'
          : '#1e293b';

  return (
    <>
      <style>{`
        @keyframes feed-row-pulse {
          0%, 100% { box-shadow: 0 0 0 1px rgba(167,139,250,0.35), 0 0 6px rgba(167,139,250,0.25); }
          50%      { box-shadow: 0 0 0 1px rgba(167,139,250,0.85), 0 0 16px rgba(167,139,250,0.55); }
        }
        .feed-row-pulse {
          animation: feed-row-pulse 1.8s ease-in-out infinite;
        }
        @keyframes feed-row-stabilizing {
          0%, 100% { opacity: 0.80; }
          50%      { opacity: 0.55; }
        }
        .feed-row-stabilizing {
          animation: feed-row-stabilizing 2.4s ease-in-out infinite;
        }
      `}</style>

      <button
        type="button"
        onClick={() => { if (!pending) onClick(marketId); }}
        className={pending ? 'feed-row-stabilizing' : momentumActive && !settled ? 'feed-row-pulse' : undefined}
        disabled={pending}
        title={pending ? 'Stabilizing… pledging unlocks once this market clears its Citizen vouch threshold.' : settled ? 'This market has settled. Click to view final state.' : undefined}
        style={{
          display:         isMobile ? 'flex' : 'grid',
          flexDirection:   isMobile ? 'column' : undefined,
          gridTemplateColumns: isMobile
            ? undefined
            : (settled
                ? 'minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr)'
                : 'minmax(0, 1fr) auto auto auto'),
          gap:             isMobile ? 8 : 16,
          alignItems:      isMobile ? 'stretch' : 'center',
          padding:         '10px 14px',
          borderRadius:    6,
          border:          `1px ${pending ? 'dashed' : 'solid'} ${borderColor}`,
          background:      pending ? '#060c16' : settled ? '#0a0f17' : active ? '#0d1c2e' : '#0a121d',
          color:           pending ? '#64748b' : '#e2e8f0',
          cursor:          pending ? 'not-allowed' : 'pointer',
          fontFamily:      'monospace',
          textAlign:       'left',
          width:           '100%',
          transition:      'border-color 0.15s ease, background 0.15s ease',
          opacity:         pending || settled ? 0.85 : 1,
        }}
      >
        {/* Title + optional lens tag */}
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{
            color:         pending ? '#94a3b8' : settled ? '#cbd5e1' : '#e2e8f0',
            fontSize:      13,
            fontWeight:    700,
            letterSpacing: '0.02em',
            whiteSpace:    isMobile ? 'normal' : 'nowrap',
            overflow:      isMobile ? 'visible' : 'hidden',
            textOverflow:  isMobile ? 'clip' : 'ellipsis',
            wordBreak:     isMobile ? 'break-word' : undefined,
          }}>
            {title}
          </span>
          {lens && (
            <span style={{
              color:         '#64748b',
              fontSize:      8,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
            }}>
              {lens}{pending ? ' · STABILIZING' : settled ? ' · SETTLED' : ''}
            </span>
          )}
          {showOracleLine && (
            <span style={{
              color:         '#94a3b8',
              fontSize:      11,
              opacity:       settled ? 0.65 : 0.85,
              whiteSpace:    isMobile ? 'normal' : 'nowrap',
              overflow:      isMobile ? 'visible' : 'hidden',
              textOverflow:  isMobile ? 'clip' : 'ellipsis',
              wordBreak:     isMobile ? 'break-word' : undefined,
              marginTop:     2,
            }}>
              Resolves per {oracleDisplayText}
              {oracleClassLabel ? ` · ${oracleClassLabel}` : ''}
              {typeof holdbackHoursOverride === 'number'
                ? ` · ${holdbackHoursOverride}h holdback`
                : ''}
            </span>
          )}
        </div>

        {/* Right-side stats. On desktop the wrapper is `display: contents`
            so its children become direct children of the grid (existing
            layout, untouched). On mobile the wrapper turns into a
            flex-wrap row that sits beneath the title, left-aligned,
            with each cell free to wrap or push to a new line rather
            than overflow the viewport. */}
        <div style={{
          display:    isMobile ? 'flex' : 'contents',
          flexWrap:   isMobile ? 'wrap' : undefined,
          gap:        isMobile ? 12 : undefined,
          alignItems: isMobile ? 'flex-start' : undefined,
          minWidth:   0,
        }}>
        {pending ? (
          // v2.3.1 — STABILIZING rail: three vouch-count cells replace
          // the pool/favourite/outsider triplet. Compact, greyed,
          // non-clickable, so the citizen can see pipeline depth
          // without mistaking it for a tradable row.
          <>
            <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
              <div style={{ color: '#fcd34d', fontSize: 10, fontWeight: 800, letterSpacing: '0.22em' }}>
                STABILIZING
              </div>
              <div style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em' }}>
                ⊡ PENDING VOUCH
              </div>
            </div>
            <div style={{ textAlign: isMobile ? 'left' : 'right', minWidth: isMobile ? 0 : 110 }}>
              <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700 }}>
                {typeof vouchCount === 'number' ? vouchCount : 0}
                {typeof vouchTarget === 'number' ? ` / ${vouchTarget}` : ''}
              </div>
              <div style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em' }}>
                VOUCHES
              </div>
            </div>
            <div style={{ textAlign: isMobile ? 'left' : 'right', minWidth: isMobile ? 0 : 110 }}>
              <div style={{ color: '#334155', fontSize: 10 }}>—</div>
              <div style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em' }}>
                PLEDGING LOCKED
              </div>
            </div>
          </>
        ) : settled ? (
          // I-065 SETTLED rail — replaces POOL/FAVOURITE/OUTSIDER on rows where
          // Arena Supabase reports the market is SETTLED within the 48h
          // visibility window. Sits in the middle column of a 3-col grid
          // (title · settled · spacer) so the SETTLED block reads as the
          // row's centred focal point rather than hugging the right edge.
          // Data-on-top (`X won`), label-on-bottom (`SETTLED · <ts>`) matches
          // the existing top-then-label rhythm of the other cells.
          <>
            <div style={{ textAlign: isMobile ? 'left' : 'center', minWidth: 0, overflow: 'hidden' }}>
              <div style={{
                color:         '#e2e8f0',
                fontSize:      15,
                fontWeight:    700,
                letterSpacing: '0.02em',
                overflow:      isMobile ? 'visible' : 'hidden',
                textOverflow:  isMobile ? 'clip' : 'ellipsis',
                whiteSpace:    isMobile ? 'normal' : 'nowrap',
                wordBreak:     isMobile ? 'break-word' : undefined,
                marginBottom:  4,
              }}>
                {winningOutcome ?? '—'}
                <span style={{ color: '#94a3b8', fontWeight: 600, marginLeft: 8 }}>won</span>
              </div>
              <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>
                <span style={{ letterSpacing: '0.22em' }}>SETTLED</span>
                {settledAt ? <span style={{ color: '#64748b' }}>{` · ${formatSettledAtUTC(settledAt)}`}</span> : null}
              </div>
            </div>
            {/* Right spacer column — preserves grid symmetry so the
                SETTLED block stays optically centred on desktop. Omit
                on mobile (flex-wrap layout, no symmetry to preserve). */}
            {!isMobile && <div />}
          </>
        ) : (
          <>
            {/* OC pool */}
            <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
              <div style={{ color: '#7dd3fc', fontSize: 12, fontWeight: 700 }}>
                {formatOC(ocVolume)} OC
              </div>
              <div style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em' }}>
                POOL
              </div>
            </div>

            {/* Favorite */}
            <div style={{ textAlign: isMobile ? 'left' : 'right', minWidth: isMobile ? 0 : 110 }}>
              {favorite ? (
                <>
                  <div style={{
                    color:        favorite.color ?? '#86efac',
                    fontSize:     11,
                    fontWeight:   700,
                    overflow:     isMobile ? 'visible' : 'hidden',
                    textOverflow: isMobile ? 'clip' : 'ellipsis',
                    whiteSpace:   isMobile ? 'normal' : 'nowrap',
                    wordBreak:    isMobile ? 'break-word' : undefined,
                  }}>
                    {favorite.label} @ {favorite.price.toFixed(2)}
                  </div>
                  <div style={{ color: '#475569', fontSize: 8, letterSpacing: '0.22em' }}>
                    FAVOURITE
                  </div>
                </>
              ) : (
                <div style={{ color: '#334155', fontSize: 10 }}>—</div>
              )}
            </div>

            {/* Deepest outsider */}
            <div style={{ textAlign: isMobile ? 'left' : 'right', minWidth: isMobile ? 0 : 110 }}>
              {outsider ? (
                <>
                  <div style={{
                    color:        outsider.color ?? '#fcd34d',
                    fontSize:     11,
                    fontWeight:   700,
                    overflow:     isMobile ? 'visible' : 'hidden',
                    textOverflow: isMobile ? 'clip' : 'ellipsis',
                    whiteSpace:   isMobile ? 'normal' : 'nowrap',
                    wordBreak:    isMobile ? 'break-word' : undefined,
                  }}>
                    {outsider.label} @ {outsider.price.toFixed(2)}
                  </div>
                  <div style={{ color: '#78350f', fontSize: 8, letterSpacing: '0.22em' }}>
                    OUTSIDER
                  </div>
                </>
              ) : (
                <div style={{ color: '#334155', fontSize: 10 }}>—</div>
              )}
            </div>
          </>
        )}
        </div>
      </button>
    </>
  );
}
